import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { errorMessage, publicApi, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { webDevice } from "../auth/device";
import { safeNext } from "../auth/next";
import { isPasskeyCancelled, passkeysSupported, signInWithPasskey } from "../auth/passkeys";
import { AuthLayout } from "../components/AuthLayout";

export function LoginPage() {
  const { session, signIn } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (session) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await signIn(unwrap(await publicApi.POST("/auth/login", { body: { email, password, device: webDevice() } })));
    } catch (err) {
      setError(errorMessage(err, { 401: "Email or password is incorrect." }));
    } finally {
      setPending(false);
    }
  }

  async function passkeySignIn() {
    setPending(true);
    setError(null);
    try {
      await signIn(await signInWithPasskey());
    } catch (err) {
      if (!isPasskeyCancelled(err)) setError(errorMessage(err, { 401: "That passkey isn't registered here." }));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout title="Sign in">
      <form onSubmit={(event) => void submit(event)} className="stack">
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending}>
          Sign in
        </button>
      </form>
      {passkeysSupported() && (
        <>
          <div className="divider">or</div>
          <button type="button" className="secondary" disabled={pending} onClick={() => void passkeySignIn()}>
            Sign in with a passkey
          </button>
        </>
      )}
      <p className="muted">
        New here? <Link to={`/signup${params.size ? `?${params.toString()}` : ""}`}>Create an account</Link>
      </p>
    </AuthLayout>
  );
}
